/**
 * ConversationMessage — single email message card in the timeline.
 * Collapsed by default; expands on click to show full body and attachments.
 *
 * Props:
 *   item       {object}   — thread item (type:'email', .email sub-object)
 *   isExpanded {bool}
 *   onToggle   {function}
 */

import { h } from 'preact'
import { useRef } from 'preact/hooks'
import { timeAgo, avatarColor, formatBytes } from './utils.js'

export function ConversationMessage({ item, isExpanded, onToggle }) {
  const email    = item.email || item
  const bodyRef  = useRef(null)
  const initials = (email.sender_person_name || email.from_name || '?').trim()[0]?.toUpperCase() || '?'
  const color    = avatarColor(email.from_name || email.sender_person_name)
  const timeStr  = timeAgo(email.sent_at || email.created_at)
  const files    = email.files || []

  return (
    <div
      class={`msg-card${isExpanded ? ' expanded' : ''}`}
      id={`support-msg-${email.id}`}
    >
      <div class="msg-card-header" onClick={onToggle} role="button" tabIndex={0}
           onKeyDown={e => e.key === 'Enter' && onToggle?.()}>

        {/* Sender avatar */}
        {email.sender_avatar_url
          ? (
            <img
              src={email.sender_avatar_url}
              width={30}
              height={30}
              class="msg-avatar"
              alt={email.from_name}
              loading="lazy"
            />
          )
          : (
            <div class="msg-avatar" style={{ background: color }}>{initials}</div>
          )
        }

        <div class="msg-body-wrapper">
          <div class="msg-sender">{email.from_name}</div>
          {email.from_address && (
            <div class="msg-address">{email.from_address}</div>
          )}
        </div>

        {/* Snippet when collapsed */}
        {!isExpanded && (
          <span class="msg-snippet">
            {email.snippet || (email.body_text || '').slice(0, 80)}
          </span>
        )}

        {/* Outbound badge */}
        {email.direction === 'outbound' && (
          <span class="msg-direction-badge">
            <i class="bi bi-arrow-up-short" /> Elküldve
          </span>
        )}

        {/* Attachment icon */}
        {(email.files_attached || files.length > 0) && (
          <i class="bi bi-paperclip msg-attach-icon" title={`${files.length || ''} melléklet`} />
        )}

        <span class="msg-time">{timeStr}</span>
        <i class={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} msg-chevron`} />
      </div>

      {isExpanded && (
        <div class="msg-card-body">
          {email.body_html
            ? (
              <div
                ref={bodyRef}
                class="email-html-body"
                dangerouslySetInnerHTML={{ __html: email.body_html }}
              />
            )
            : (
              <pre class="msg-body-text">
                {email.body_text}
              </pre>
            )
          }

          {files.length > 0 && (
            <div class="msg-attachments">
              <div class="msg-attachments-label">
                <i class="bi bi-paperclip" /> Mellékletek ({files.length})
              </div>
              <ul class="msg-attachments-list">
                {files.map(file => (
                  <li key={file.id} class="msg-attachment-item">
                    <i class={`bi ${fileIcon(file.content_type)} msg-attachment-icon`} />
                    <a
                      href={file.url}
                      download={file.filename}
                      class="msg-attachment-name"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.filename}
                    </a>
                    <span class="msg-attachment-size">{formatBytes(file.byte_size)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function fileIcon(contentType) {
  if (!contentType) return 'bi-file-earmark'
  if (contentType.startsWith('image/'))       return 'bi-file-earmark-image'
  if (contentType === 'application/pdf')      return 'bi-file-earmark-pdf'
  if (contentType.includes('word'))           return 'bi-file-earmark-word'
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'bi-file-earmark-excel'
  if (contentType.includes('zip') || contentType.includes('compressed'))    return 'bi-file-earmark-zip'
  return 'bi-file-earmark'
}
